import { FetcherProvider } from "@website/fetcher/provider";
import { useAuthPages } from "@website/hooks/auth-manager";
import { LocalStorageProvider } from "@website/hooks/local-storage";
import { startUserTracking } from "@website/tracking/user";
import { SnackbarProvider } from "@website/ui/snackbar";
import "@website/ui/styles";
import { getChannel } from "@website/utils/channel/get-channel";
import { Channels } from "@website/utils/channel/types";
import { SERVER_NOW } from "@website/utils/server-now";
import { replaceSnappTripBasedOnChannel } from "@website/utils/title-replace";
import { Scripts } from "@website/widget/scripts/scripts";
import App, { AppContext, AppProps } from "next/app";
import Head from "next/head";
import { Layout } from "../components";
import GrowthBookWrapper from "../components/growthbook/GrowthBookWrapper";

startUserTracking({ product: "core" });

const MyApp = ({ Component, pageProps }: AppProps) => {
  useAuthPages();

  const webEngageLicenseCode: string = {
    android: "11b5650c0",
    ios: "11b5650c0",
    jek: "~47b6574d",
    ap: "11b5650c0",
    app: "11b5650c0",
    ads: "11b5650c0",
    "snapp-ir": "~47b6574d",
    snapp: "~47b6574d",
    website: "11b5650c0",
    telesale: "11b5650c0"
  }[getChannel() as Channels];

  const gtmLicenseCode: string = {
    android: "GTM-MT54VMN",
    ios: "GTM-KJPQFH4",
    jek: "GTM-P8WGVRK",
    ap: "GTM-KJPQFH4",
    app: "GTM-KJPQFH4",
    ads: "GTM-KJPQFH4",
    "snapp-ir": "GTM-5HTF8BD",
    snapp: "GTM-P8WGVRK",
    website: "GTM-MT54VMN",
    telesale: "GTM-MT54VMN"
  }[getChannel() as Channels];

  const serverNow = SERVER_NOW();

  return (
    <>
      <Head>
        <title>{replaceSnappTripBasedOnChannel("SnappTrip Profile")}</title>
        {pageProps?.seoData?.robots ? (
          <meta content={pageProps?.seoData?.robots} name="robots" />
        ) : (
          getChannel() !== "web" && (
            <meta content="noindex,nofollow" name="robots" />
          )
        )}
        <meta
          content={replaceSnappTripBasedOnChannel("SnappTrip dashboard")}
          name="description"
        />
      </Head>
      <SnackbarProvider>
        <FetcherProvider>
          <LocalStorageProvider prefix="profile" version={1}>
            <GrowthBookWrapper>
              <Layout>
                <Component {...pageProps} />
              </Layout>
            </GrowthBookWrapper>
          </LocalStorageProvider>
        </FetcherProvider>
      </SnackbarProvider>
      <Scripts
        gtmLicence={gtmLicenseCode}
        serverNow={serverNow}
        webengageLicence={webEngageLicenseCode}
      />
    </>
  );
};

MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProperties = await App.getInitialProps(appContext);

  return {
    ...appProperties,
    pageProps: {
      ...appProperties.pageProps,
      serverNow: SERVER_NOW()
    }
  };
};

export default MyApp;
